import { useMemo, useState } from 'react'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { useAuth } from '@/shared/auth/useAuth'
import { useIsPlatformOwner } from '@/shared/auth/useIsPlatformOwner'
import { usePermissions } from '@/shared/auth/usePermissions'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { UrlSearchInput } from '@/shared/ui/UrlSearchInput'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { RowActions } from '@/shared/ui/table/RowActions'
import { TenantCredentialsModal } from '@/features/tenants/TenantCredentialsModal'
import { TenantFormModal } from '@/features/tenants/TenantFormModal'
import {
  TENANT_STATES,
  TENANT_STATE_ALL,
  TENANT_STATE_FILTER,
  fetchTenants,
  setTenantState,
  tenantStateFilter,
  type TenantEntry,
  type TenantProvisionResult,
} from '@/features/tenants/tenantsApi'

/**
 * Tashkilotlar reyestri — <b>platforma egasi uchun</b>.
 *
 * <h2>Nega bu sahifa paydo bo'ldi</h2>
 * Platforma ko'p ijarachiga sotiladi, lekin yangi ISP yoki mehmonxonani
 * ulashning yagona yo'li QO'LDA SQL edi: `/api/tenants` da faqat `GET`
 * bor edi va u gateway orqali umuman chiqarilmagandi.
 *
 * <h2>⚠️ `DataTable` EMAS, oddiy `Table`</h2>
 * `GET /api/tenants` sahifalanmaydi va sahifalanmasligi ham kerak:
 * tashkilotlar soni o'nlab, yuzlab emas. `DataTable` esa server
 * sahifalashini talab qiladi — bu yerda u ortiqcha murakkablik
 * bo'lardi. `UsersPage` bilan bir xil sabab va bir xil naqsh.
 *
 * <h2>⚠️ O'CHIRISH tugmasi YO'Q</h2>
 * Bu unutilgan emas. Tashkilotda abonentlar, o'zgarmas moliyaviy jurnal
 * va audit bor; `DELETE` esa `ON DELETE CASCADE` bo'ylab identity
 * yozuvlarini olib ketardi — ya'ni "kim nima qilgan edi" degan savolga
 * javob beradigan yozuvlar yo'qolardi. O'rniga "to'xtatish" bor va u
 * qaytariladi.
 */
export function TenantsPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { user } = useAuth()
  const isPlatformOwner = useIsPlatformOwner()
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<TenantEntry | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [credentials, setCredentials] = useState<TenantProvisionResult | null>(null)

  /*
    ⚠️ QIDIRUV VA FILTR HOLATI MANZILDA. Ilgari qidiruv `useState` da
    edi va sahifa yangilanganda yo'qolardi.

    ⚠️ Ikkalasi ham MIJOZDA ishlaydi va bu yolg'on emas:
    `GET /api/tenants` (`TenantAdminResource#list()`) birorta parametr
    qabul qilmaydi va BARCHA tashkilotlarni bitta ro'yxatda beradi.
    Ya'ni filtr to'liq ma'lumot ustidan yuradi.
  */
  const { state: tableState, patch: patchTable, reset: resetTable } = useTableUrlState()
  const search = tableState.search
  /**
   * ⚠️ Sukut — FAQAT AMALDAGILAR (`tenantStateFilter`). To'xtatilgan
   * tashkilot yashirinadi, lekin YO'QOLMAYDI: filtrda «To'xtatilgan»
   * tanlanadi va uni QAYTA YOQISH amali o'sha qatorda turadi.
   */
  const stateFilter = tenantStateFilter(tableState)
  /** Operator ATAYLAB tanladimi — sukutdagi torayish «filtr» deb sanalmaydi */
  const picked = tableState.filters[TENANT_STATE_FILTER]?.[0] !== undefined

  const showAll = () =>
    patchTable({ filters: { ...tableState.filters, [TENANT_STATE_FILTER]: [TENANT_STATE_ALL] } })

  const canWrite = can('tenant.create') && isPlatformOwner
  const canUpdate = can('tenant.update') && isPlatformOwner

  const tenants = useQuery({
    queryKey: queryKeys.tenants.all,
    queryFn: ({ signal }) => fetchTenants(signal),
    enabled: can('tenant.read') && isPlatformOwner,
  })

  const changeState = useMutation({
    mutationFn: ({ id, state, reason }: { id: string; state: 'ACTIVE' | 'SUSPENDED'; reason?: string }) =>
      setTenantState(id, state, reason),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all })
      void message.success(
        row.state === 'SUSPENDED' ? t('tenants.suspended') : t('tenants.activated'),
      )
    },
    onError: (e: unknown) => setError(translateError(e, t)),
  })

  const all = useMemo(() => tenants.data ?? [], [tenants.data])
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return all.filter((row) => {
      if (stateFilter !== TENANT_STATE_ALL && row.state !== stateFilter) return false
      if (q === '') return true
      return (
        row.code.toLowerCase().includes(q)
        || row.name.toLowerCase().includes(q)
        || (row.realm ?? '').toLowerCase().includes(q)
      )
    })
  }, [all, search, stateFilter])

  const isFiltered = search.trim() !== '' || picked
  /**
   * ⚠️ Sukut torayish ham SANALADI. «5 / 8» nishoni yagona joy bo'lib,
   * uchtasi ko'rinmayotganini aytadi — usiz to'xtatilgan tashkilot
   * jimgina yo'qolardi va operator uni o'chib ketgan deb o'ylardi.
   */
  const hidden = all.length - rows.length

  const columns = useMemo<ColumnsType<TenantEntry>>(
    () => [
      {
        // Sahifalanmagan ro'yxat — raqam to'g'ridan-to'g'ri `index` dan
        key: '__rowNo',
        title: t('dataTable.rowNo'),
        width: 56,
        align: 'center',
        render: (_v: unknown, _r: TenantEntry, index: number) => (
          <Typography.Text type="secondary">{index + 1}</Typography.Text>
        ),
      },
      {
        title: t('tenants.code'),
        dataIndex: 'code',
        render: (code: string, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{code}</Typography.Text>
            {row.id === user?.tenant.id ? (
              <Tag color="blue">{t('tenants.current')}</Tag>
            ) : null}
          </Space>
        ),
      },
      { title: t('tenants.name'), dataIndex: 'name' },
      {
        title: t('tenants.businessKind'),
        dataIndex: 'businessKind',
        render: (kind: string) => t(`businessKind.${kind}`),
      },
      { title: t('tenants.currency'), dataIndex: 'currency' },
      { title: t('tenants.dayTimezone'), dataIndex: 'dayTimezone' },
      {
        // ⚠️ Ro'yxatda ATAYLAB ko'rsatiladi: abonent RADIUS'ga
        // `login@realm` bo'lib boradi va bu qiymat boshqa hech qayerda
        // ko'rinmaydi. Uni bilmaslik yangi tashkilotdagi birinchi
        // ulanish muammosining eng ko'p uchraydigan sababi.
        title: t('tenants.realm'),
        dataIndex: 'realm',
        // ⚠️ Realm endi ixtiyoriy. Bo'sh katak "sozlanmagan" degan
        // TAXMIN qoldirardi; aslida bu ONGLI holat — bitta tashkilotli
        // o'rnatmada login RADIUS'ga TOZA boradi. Shuning uchun matn
        // bilan aytiladi.
        render: (realm: string | null | undefined) =>
          realm ? (
            <Typography.Text code>{realm}</Typography.Text>
          ) : (
            <Typography.Text type="secondary">{t('tenants.realmNone')}</Typography.Text>
          ),
      },
      {
        title: t('tenants.members'),
        dataIndex: 'memberCount',
        render: (n: number) =>
          n === 0 ? (
            <Tooltip title={t('tenants.noMembersHint')}>
              <Tag color="red">0</Tag>
            </Tooltip>
          ) : (
            n
          ),
      },
      {
        title: t('tenants.state'),
        dataIndex: 'state',
        render: (state: string) => (
          <Tag color={state === 'ACTIVE' ? 'green' : 'orange'}>{t(`tenantState.${state}`)}</Tag>
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_: unknown, row) => (
          // Huquq bu sahifada `Can` bilan emas, `canUpdate` bayrog'i bilan
          // tekshirilardi (GLOBAL doira ham shart) — shakl saqlanadi:
          // amal KO'RINADI, lekin o'chirilgan holatda.
          <RowActions
            items={[
              {
                key: 'edit',
                label: t('tenants.edit'),
                onClick: () => { setEditing(row); setFormOpen(true) },
                disabled: !canUpdate,
              },
              {
                // ⚠️ O'Z tashkilotini to'xtatib bo'lmaydi: operator
                // shu zahoti o'zini panelga kirolmaydigan qilib
                // qo'yardi va uni qaytarishning yagona yo'li qo'lda
                // SQL bo'lardi. Bu `UsersPage` dagi "o'zini bloklab
                // bo'lmaydi" qoidasi bilan bir xil.
                key: 'suspend',
                label:
                  row.id === user?.tenant.id ? (
                    <Tooltip title={t('tenants.cannotSuspendSelf')}>{t('tenants.suspend')}</Tooltip>
                  ) : (
                    t('tenants.suspend')
                  ),
                onClick: () => {
                  modal.confirm({
                    title: t('tenants.suspendConfirmTitle', { code: row.code }),
                    content: t('tenants.suspendConfirmBody'),
                    okText: t('tenants.suspend'),
                    okButtonProps: { danger: true },
                    cancelText: t('common.cancel'),
                    onOk: () => changeState.mutate({ id: row.id, state: 'SUSPENDED' }),
                  })
                },
                disabled: !canUpdate || row.id === user?.tenant.id,
                loading: changeState.isPending,
                danger: true,
                hidden: row.state !== 'ACTIVE',
              },
              {
                /*
                  ⚠️ TASDIQLASH QO'SHILDI. Amal qaytariladi (to'xtatish
                  yana bor), lekin u BUTUN tashkilotni yoqadi: uning
                  xodimlari panelga qayta kira boshlaydi. Bir bosishda
                  ketadigan bunday amal menyuda tasodifan bosilishi
                  mumkin va tasdiqlash matni nima bo'lishini aytadi.
                */
                key: 'activate',
                label: t('tenants.activate'),
                onClick: () => {
                  modal.confirm({
                    title: t('tenants.activateConfirmTitle', { code: row.code }),
                    content: t('tenants.activateConfirmBody'),
                    okText: t('tenants.activate'),
                    cancelText: t('common.cancel'),
                    onOk: () => changeState.mutate({ id: row.id, state: 'ACTIVE' }),
                  })
                },
                disabled: !canUpdate,
                loading: changeState.isPending,
                hidden: row.state === 'ACTIVE',
              },
            ]}
          />
        ),
      },
    ],
    [t, canUpdate, changeState, modal, user?.tenant.id],
  )

  // ⚠️ Huquq bor, lekin GLOBAL doira yo'q — bu holat ALOHIDA
  // ko'rsatiladi. Bo'sh jadval "tashkilot yo'q" degan noto'g'ri
  // xulosaga olib kelardi, backend esa 403 qaytaradi.
  if (!isPlatformOwner) {
    return (
      <>
        <PageHeader title={t('nav.tenants')} description={t('pageIntro.tenants')} />
        <Alert type="warning" showIcon message={t('tenants.globalScopeRequired')} />
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={t('nav.tenants')}
        description={t('pageIntro.tenants')}
        extra={
          canWrite ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { setEditing(null); setFormOpen(true) }}
            >
              {t('tenants.create')}
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {tenants.isError ? (
        <Alert
          type="error"
          showIcon
          message={translateError(tenants.error, t)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Space wrap style={{ marginBottom: 16 }}>
        <UrlSearchInput placeholder={t('tenants.searchPlaceholder')} width={300} />
        {/*
          Holat filtri — "qaysi tashkilot to'xtatilgan" eng ko'p
          beriladigan savol va u bir qarashda ko'rinmaydi.

          ⚠️ `allowClear` YO'Q: bo'sh maydon «filtr qo'yilmagan» deb
          o'qilardi, ro'yxat esa aslida faqat faollarni ko'rsatib turgan
          bo'lardi.
        */}
        <Tooltip title={t('dataTable.closedHiddenHint')}>
          <Select<string>
            style={{ minWidth: 180 }}
            aria-label={t('tenants.state')}
            value={stateFilter}
            onChange={(v: string) =>
              patchTable({ filters: { ...tableState.filters, [TENANT_STATE_FILTER]: [v] } })
            }
            options={[
              ...TENANT_STATES.map((s) => ({ value: s, label: t(`tenantState.${s}`) })),
              { value: TENANT_STATE_ALL, label: t('dataTable.stateAll') },
            ]}
          />
        </Tooltip>
        {hidden > 0 || isFiltered ? (
          <Space size={4}>
            <Tag color="blue">{t('roles.shownOfTotal', { shown: rows.length, total: all.length })}</Tag>
            {isFiltered ? (
              <Button size="small" type="link" onClick={resetTable}>
                {t('dataTable.clearFilters')}
              </Button>
            ) : (
              <Button size="small" type="link" onClick={showAll}>
                {t('dataTable.stateAll')}
              </Button>
            )}
          </Space>
        ) : null}
      </Space>

      <Table<TenantEntry>
        rowKey={(row) => row.id}
        columns={columns}
        dataSource={rows}
        loading={tenants.isLoading}
        /*
          ⚠️ SAHIFALASH YO'Q: `GET /api/tenants` uni qo'llab-quvvatlamaydi
          va tashkilotlar soni o'nlab. To'liq ro'yxatni olib mijozda
          kesish "sahifalash bor" degan soxta taassurot berardi.
        */
        pagination={false}
        size="small"
        scroll={{ x: 1100 }}
        locale={{
          // ⚠️ Ikki xil bo'shlik ajratilgan — sabab `RolesPage` dagi
          //    bir xil blok izohida.
          //
          // ⚠️ UCHINCHI SHOX QO'SHILDI: sukut ko'rinishda to'xtatilganlar
          //    yashirin, ya'ni bo'sh jadval «tashkilot yo'q» degani EMAS
          //    va tuzatish yo'li yaratish emas, «Barcha holatlar».
          emptyText: (
            <EmptyState
              variant="no-data"
              title={isFiltered ? t('dataTable.emptyFiltered') : t('tenants.emptyTitle')}
              description={isFiltered ? t('dataTable.emptyFilteredHint') : t('tenants.emptyHint')}
              {...(isFiltered
                ? { actionLabel: t('dataTable.resetFilters'), onAction: resetTable }
                : hidden > 0
                  ? { actionLabel: t('dataTable.stateAll'), onAction: showAll }
                  : canWrite
                    ? {
                        actionLabel: t('tenants.create'),
                        onAction: () => {
                          setEditing(null)
                          setFormOpen(true)
                        },
                      }
                    : {})}
            />
          ),
        }}
      />

      <TenantFormModal
        open={formOpen}
        tenant={editing}
        onClose={() => setFormOpen(false)}
        onProvisioned={setCredentials}
      />
      <TenantCredentialsModal result={credentials} onClose={() => setCredentials(null)} />
    </>
  )
}
